import React, { useCallback, useContext, useId, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { buildAccommodationCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'
import { rem } from 'polished'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { Refundable } from 'components/Common/CancellationPolicies/utils/getCancellationPolicyStatus'
import Clickable from 'components/Common/Clickable'
import Modal from 'LuxKit/Modal/Modal'
import HelpPane from 'components/Common/HelpPane'
import ModalContext from 'contexts/ModalContext'

/**
 * Default timezone used when property timezone is not available.
 * Using UTC as a neutral default that works across all regions.
 */
const DEFAULT_PROPERTY_TIMEZONE = 'UTC'

const StyledRadioInput = styled(RadioInput)`
  display: flex;
  align-items: start;
`

const CancellationClickable = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  text-decoration: none;
`

interface Props {
  rates: Array<App.PropertyRate>
  selectedRateId: string | undefined
  onChange: (rateId: string) => void
  propertyTimezone: string
  staffRateLabelType?: StaffRateLabelType
  ratePackageNameByRateId?: Record<string, string>
  showStaffLabels: boolean
}

type StaffRateLabelType = 'lpp' | 'lpc'

/**
 * Get cancellation policy display text
 */
interface CancellationDisplayText {
  primary: string
  secondary?: string
  highlightPrimary: boolean
}

function getStaffRateLabel(
  intl: ReturnType<typeof useIntl>,
  staffRateLabelType: StaffRateLabelType | undefined,
  showStaffLabels: boolean,
  rateServiceType: App.Property.ServiceRateDetails['type'],
  rateName?: string,
  fallbackPackageName?: string,
) {
  if (!showStaffLabels) {
    return undefined
  }

  if (rateServiceType === 'bedbank') {
    return intl.formatMessage({
      id: 'YBQyI5fJRZ',
      defaultMessage: 'Bedbank rate (label only shows for staff)',
      description: 'Staff-only label for bedbank rate',
    })
  }

  if (!staffRateLabelType) {
    return undefined
  }

  const packageName = rateName || fallbackPackageName
  const rateTypeLabel = staffRateLabelType === 'lpp' ? 'LPP' : 'LPC'

  if (!packageName) {
    return intl.formatMessage(
      {
        id: 'Bp3Dx8NUvk',
        defaultMessage: '{rateType} rate (label only shows for staff)',
        description: 'Staff-only label for LPC/LPP rate without package name',
      },
      { rateType: rateTypeLabel },
    )
  }

  return intl.formatMessage(
    {
      id: '4vraZ23cCP',
      defaultMessage: '{rateType} rate - {packageName} (label only shows for staff)',
      description: 'Staff-only label for LPC/LPP rate with package name',
    },
    { rateType: rateTypeLabel, packageName },
  )
}

function getCancellationText(
  intl: ReturnType<typeof useIntl>,
  rate: App.PropertyRate,
  timezone: string,
): CancellationDisplayText {
  const { headlineCancellationPolicyType, headlineCancellationPolicyStart } = rate

  if (headlineCancellationPolicyType === 'nonRefundable') {
    return {
      primary: Refundable.Non,
      highlightPrimary: false,
    }
  }

  const refundText = headlineCancellationPolicyType === 'fullyRefundable'
    ? Refundable.Fully
    : Refundable.Partial

  // Format date if available
  if (headlineCancellationPolicyStart) {
    const formattedDate = formatDate.formattedShortDateByTimezone(
      headlineCancellationPolicyStart,
      timezone || DEFAULT_PROPERTY_TIMEZONE,
    )

    return {
      primary: refundText,
      secondary: intl.formatMessage(
        {
          id: 'uO5LMI6zaa',
          defaultMessage: 'until {date}',
          description: 'Date suffix for cancellation policy',
        },
        { date: formattedDate },
      ),
      highlightPrimary: true,
    }
  }

  return {
    primary: refundText,
    highlightPrimary: true,
  }
}

function PropertyRefundabilitySelector(props: Props) {
  const {
    rates,
    selectedRateId,
    onChange,
    propertyTimezone,
    staffRateLabelType,
    ratePackageNameByRateId,
    showStaffLabels,
  } = props

  const intl = useIntl()
  const radioGroupName = useId()
  const showModal = useContext(ModalContext)

  const selectedRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rateId = event.target.value
    onChange(rateId)
  }, [onChange])

  const handleShowCancellationDetails = useCallback((rate: App.PropertyRate | undefined, label: string) => {
    if (!rate) {
      return
    }

    const { description: policyDescriptions } = buildAccommodationCancellationPolicies({
      fullRefundableUntil: rate.fullyRefundableUntil,
      cancellationPolicies: rate.cancellationPolicies.map(({
        startIsoDateTime,
        endIsoDateTime,
        penaltyPrice,
        isFullyNonRefundable,
        originalDescription,
      }) => ({
        amount: penaltyPrice,
        percent: 0,
        description: originalDescription,
        start: startIsoDateTime,
        end: endIsoDateTime,
        nonRefundable: isFullyNonRefundable,
      })),
    }, {
      timezone: propertyTimezone,
      currencyCode: rate.currency,
      hideExcludesFlights: !rate.includesFlights,
    })

    showModal(<Modal title={label}>
      {policyDescriptions.map((line, index) => <BodyText variant="large" as="p" key={index}>{line}</BodyText>)}
      <HelpPane />
    </Modal>)
  }, [propertyTimezone, showModal])

  if (rates.length === 0) {
    return null
  }

  return (
    <RadioGroup>
      {rates.map((rate) => {
        const text = getCancellationText(intl, rate, propertyTimezone)
        const isSelected = rate.rateId === selectedRateId
        const fallbackPackageName = ratePackageNameByRateId?.[rate.rateId]
        const staffRateLabel = getStaffRateLabel(
          intl,
          staffRateLabelType,
          showStaffLabels,
          rate.service.type,
          rate.name,
          fallbackPackageName,
        )
        const shouldShowStaffRateLabel = !!staffRateLabel
        // Use luxPlusTotals (membership price) if available, otherwise use regular totals
        const ratePrice = rate.luxPlusTotals?.sellingPrice ?? rate.totals.sellingPrice
        const selectedRatePrice = selectedRate?.luxPlusTotals?.sellingPrice ?? selectedRate?.totals.sellingPrice
        const priceDifference = selectedRate && rate.rateId !== selectedRate.rateId && selectedRatePrice
          ? ratePrice - selectedRatePrice
          : 0

        return (
          <StyledRadioInput
            key={rate.rateId}
            name={radioGroupName}
            value={rate.rateId}
            onChange={handleChange}
            checked={isSelected}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center" wrap="wrap">
              <BodyText
                variant="medium"
                weight={text.highlightPrimary ? 'bold' : undefined}
                colour={text.highlightPrimary ? 'highlight-secondary' : undefined}
                wrap="no-wrap"
              >
                {text.primary}
              </BodyText>
              {text.secondary && (
                <BodyText variant="medium" wrap="no-wrap">
                  {text.secondary}
                </BodyText>
              )}
              {shouldShowStaffRateLabel && (
                <BodyText variant="medium" colour="neutral-three" wrap="no-wrap">
                  {staffRateLabel}
                </BodyText>
              )}
              {!isSelected && priceDifference !== 0 && (
                <BodyText variant="medium" colour="neutral-two" weight="bold" wrap="no-wrap">
                  {priceDifference > 0 ? '+' : ''}
                  <FormatCurrency value={priceDifference} format="roundedDollar" />
                </BodyText>
              )}
              <CancellationClickable
                onClick={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                  handleShowCancellationDetails(rate, text.primary)
                }}
              >
                <LineInfoCircleIcon size="XS" />
              </CancellationClickable>
            </Group>
          </StyledRadioInput>
        )
      })}
    </RadioGroup>
  )
}

export default PropertyRefundabilitySelector
